import { Controller } from "@hotwired/stimulus"

// 작업자 호소 form — 계단식 활성화 컨트롤러
//   호소 유무 radio = 'true' (호소 있음) → 호소 유형 checkbox 영역 활성
//     ├─ '증상 호소 있음' 체크 → 호소 증상 select 활성
//     └─ 호소 유형 중 어느 하나라도 체크 → 메모 textarea 활성
//   호소 유무 radio = 'false' (호소 없음) → 그 외 모두 비활성 + 값 reset
//
// form 루트의 data-action="change->...#onChange" 가 모든 input change 를 받아
// input.name 으로 분기 처리한다.
export default class extends Controller {
  static targets = ["typeFieldset", "typeSymptom", "typeExposure", "typeOther", "symptomSelect", "memoTextarea", "typeRequiredMark", "symptomRequiredMark"]

  connect() {
    this.refresh()
  }

  // form 내부의 모든 change event 처리 — input.name 으로 핸들러 분기.
  onChange(event) {
    const name = event.target.name
    if (!name) return

    if (name.endsWith("[has_complaint]")) {
      this._handleHasComplaintChange(event)
    } else if (this._isTypeCheckbox(name)) {
      this.refresh()
    }
  }

  _isTypeCheckbox(name) {
    return name.endsWith("[has_symptom_complaint]") ||
           name.endsWith("[has_exposure_concern]") ||
           name.endsWith("[has_other_complaint]")
  }

  // radio (호소 유무) 변경 시 — '호소 없음' 이면 type checkbox / symptom / memo 모두 reset
  _handleHasComplaintChange(event) {
    const hasComplaint = event.target.value === "true"
    if (!hasComplaint) {
      this._eachTypeCheckbox(cb => { cb.checked = false })
      if (this.hasSymptomSelectTarget) this._clearSelect(this.symptomSelectTarget)
      if (this.hasMemoTextareaTarget) this.memoTextareaTarget.value = ""
    }
    this.refresh()
  }

  refresh() {
    const hasComplaint = this._hasComplaintValue()
    const symptomChecked = this.hasTypeSymptomTarget && this.typeSymptomTarget.checked
    const anyTypeChecked = this._anyTypeChecked()

    // 1) 호소 유형 영역 — 호소 있음일 때만
    this._setFieldsetEnabled(hasComplaint)

    // 2) 호소 증상 select — 호소 있음 + 증상 호소 있음 체크
    if (this.hasSymptomSelectTarget) {
      this._setSelectEnabled(this.symptomSelectTarget, hasComplaint && symptomChecked)
    }

    // 3) 메모 textarea — 호소 있음 + 호소 유형 중 하나라도 체크
    if (this.hasMemoTextareaTarget) {
      this.memoTextareaTarget.disabled = !(hasComplaint && anyTypeChecked)
    }

    // 4) 별표 표시:
    //    - [호소 유형] *: '호소 있음' 선택 시
    //    - [호소 증상] *: '호소 있음' + '증상 호소 있음' 체크 시
    if (this.hasTypeRequiredMarkTarget) {
      this.typeRequiredMarkTargets.forEach((m) => m.classList.toggle("hidden", !hasComplaint))
    }
    if (this.hasSymptomRequiredMarkTarget) {
      this.symptomRequiredMarkTargets.forEach((m) => m.classList.toggle("hidden", !(hasComplaint && symptomChecked)))
    }
  }

  _hasComplaintValue() {
    const radio = this.element.querySelector('input[type="radio"][name$="[has_complaint]"]:checked')
    return radio ? radio.value === "true" : false
  }

  _eachTypeCheckbox(fn) {
    [this.hasTypeSymptomTarget && this.typeSymptomTarget,
     this.hasTypeExposureTarget && this.typeExposureTarget,
     this.hasTypeOtherTarget && this.typeOtherTarget]
      .filter(Boolean).forEach(fn)
  }

  _anyTypeChecked() {
    return (this.hasTypeSymptomTarget && this.typeSymptomTarget.checked) ||
           (this.hasTypeExposureTarget && this.typeExposureTarget.checked) ||
           (this.hasTypeOtherTarget && this.typeOtherTarget.checked)
  }

  _setFieldsetEnabled(enabled) {
    if (!this.hasTypeFieldsetTarget) return
    this.typeFieldsetTarget.classList.toggle("opacity-50", !enabled)
    this.typeFieldsetTarget.classList.toggle("pointer-events-none", !enabled)
    this._eachTypeCheckbox(cb => { cb.disabled = !enabled })
  }

  // Fomantic UI dropdown 의 select 활성/비활성: hidden input.disabled + .ui.dropdown class toggle
  _setSelectEnabled(selectEl, enabled) {
    selectEl.disabled = !enabled
    const dropdown = selectEl.closest(".ui.dropdown")
    if (dropdown) {
      dropdown.classList.toggle("disabled", !enabled)
    }
    if (!enabled) this._clearSelect(selectEl)
  }

  _clearSelect(selectEl) {
    selectEl.value = ""
    const dropdown = selectEl.closest(".ui.dropdown")
    if (dropdown) {
      const textEl = dropdown.querySelector(".text")
      if (textEl) {
        textEl.textContent = textEl.dataset.placeholder || textEl.textContent
        textEl.classList.add("default")
      }
    }
  }
}
